// utils/ablyClientManager.js - Utility for managing Ably connections with better error handling
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

class AblyClientManager {
  constructor() {
    this.client = null;
    this.initializationPromise = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
  }

  async initialize() {
    if (this.client && this.client.connection.state === 'connected') {
      return this.client;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeClient();
    return this.initializationPromise;
  }

  async _initializeClient() {
    try {
      if (enableDevTools) console.log('Initializing Ably client...');

      // Import Ably dynamically
      const Ably = await import('ably');

      // Get token from our token endpoint
      if (enableDevTools) console.log('Fetching Ably token...');
      const tokenResponse = await fetch('/.netlify/functions/ably-public-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: `background-client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        console.error('Token fetch failed:', errorData);
        throw new Error(`Failed to get Ably token: ${errorData.message || tokenResponse.statusText}`);
      }

      const tokenData = await tokenResponse.json();
      if (enableDevTools) console.log('Token response:', tokenData);

      if (!tokenData.success || !tokenData.tokenRequest) {
        console.error('Invalid token response:', tokenData);
        throw new Error('Invalid token response format');
      }

      if (enableDevTools) console.log('Creating Ably client with token...');
      this.client = new Ably.Realtime({
        authCallback: (tokenParams, callback) => {
          // Use the token we just fetched
          callback(null, tokenData.tokenRequest);
        },
        autoConnect: true,
        disconnectedRetryTimeout: 5000,
        suspendedRetryTimeout: 10000,
        logLevel: enableDevTools ? 1 : 0, // Enable debug logging only with dev tools
        // Connection stability settings
        closeOnUnload: true,
        queryTime: true,
      });

      // Set up connection event listeners with conditional logging
      this.client.connection.on('connected', () => {
        if (enableDevTools) console.log('Ably client connected successfully');
        this.reconnectAttempts = 0;
      });

      this.client.connection.on('connecting', () => {
        if (enableDevTools) console.log('Ably client connecting...');
      });

      this.client.connection.on('disconnected', (stateChange) => {
        const reason = stateChange.reason?.message || '';
        if (reason.includes('idle') || reason.includes('timeout')) {
          if (enableDevTools) console.log('🔄 Background Ably client disconnected due to idle timeout, will auto-reconnect');
        } else {
          if (enableDevTools) console.warn('Ably client disconnected:', stateChange);
        }
      });

      this.client.connection.on('failed', (stateChange) => {
        console.error('Ably client connection failed:', stateChange);
        if (stateChange.reason) {
          console.error('Failure reason:', stateChange.reason);
        }
        this.client = null;
        this.initializationPromise = null;
      });

      this.client.connection.on('suspended', (stateChange) => {
        const reason = stateChange.reason?.message || '';
        if (reason.includes('idle') || reason.includes('timeout')) {
          if (enableDevTools) console.log('🔄 Background Ably client suspended due to idle timeout, attempting reconnection');
          // Try to force reconnection if suspended due to timeout
          setTimeout(() => {
            if (this.client && this.client.connection.state === 'suspended') {
              this.client.connect();
            }
          }, 1000);
        } else {
          if (enableDevTools) console.warn('Ably client connection suspended:', stateChange);
        }
      });

      this.client.connection.on('update', (stateChange) => {
        if (enableDevTools) console.log('Ably connection update:', stateChange);
      });

      // Wait for connection to be established
      if (enableDevTools) console.log('Waiting for Ably connection...');
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('Ably connection timeout');
          reject(new Error('Ably connection timeout'));
        }, 15000); // 15 second timeout

        this.client.connection.once('connected', () => {
          if (enableDevTools) console.log('Ably connection established');
          clearTimeout(timeout);
          resolve();
        });

        this.client.connection.once('failed', (stateChange) => {
          console.error('Ably connection failed during setup:', stateChange);
          clearTimeout(timeout);
          reject(new Error(`Ably connection failed: ${stateChange.reason ? stateChange.reason.message : 'Unknown error'}`));
        });

        this.client.connection.once('suspended', (stateChange) => {
          console.error('Ably connection suspended during setup:', stateChange);
          clearTimeout(timeout);
          reject(new Error(`Ably connection suspended: ${stateChange.reason ? stateChange.reason.message : 'Unknown error'}`));
        });
      });

      return this.client;

    } catch (error) {
      console.error('Failed to initialize Ably client:', error);
      this.client = null;
      this.initializationPromise = null;

      // Retry logic
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        if (enableDevTools) console.log(`Retrying Ably initialization (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 2000 * this.reconnectAttempts));
        return this._initializeClient();
      }

      throw error;
    }
  }

  async getChannel(channelName) {
    const client = await this.initialize();
    return client.channels.get(channelName);
  }

  async close() {
    if (this.client) {
      this.client.close();
      this.client = null;
      this.initializationPromise = null;
    }
  }

  isConnected() {
    return this.client && this.client.connection.state === 'connected';
  }

  getConnectionState() {
    return this.client ? this.client.connection.state : 'closed';
  }
}

// Create a singleton instance
const ablyClientManager = new AblyClientManager();

export default ablyClientManager;
